import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import {
  useCallback,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils";

/**
 * Which mark of a chart is being read, and the card that reads it.
 *
 * One card per chart, moved from mark to mark, instead of a popover per mark:
 * thirty columns or twenty runs stay thirty spans, and the card does not
 * re-enter between neighbours. The pointer and the arrow keys drive the same
 * cursor, so every datum a mouse can read is reachable from the keyboard, and
 * the card never takes focus away from the chart.
 */

interface Active {
  index: number;
  anchor: Element;
}

export function useChartCursor(count: number) {
  const [active, setActive] = useState<Active | null>(null);
  const marks = useRef<Array<Element | null>>([]);

  const select = useCallback((index: number) => {
    const anchor = marks.current[index];

    if (anchor) setActive({ index, anchor });
  }, []);

  const clear = useCallback(() => setActive(null), []);

  /**
   * Spread on each mark: it registers itself and answers the pointer. A chart
   * with nothing to read (count 0, or drawn inside another control) leaves its
   * marks inert.
   */
  const mark = (index: number) =>
    count === 0
      ? { ref: undefined, onPointerEnter: undefined }
      : {
          ref: (element: Element | null) => {
            marks.current[index] = element;
          },
          onPointerEnter: (event: { currentTarget: Element }) =>
            setActive({ index, anchor: event.currentTarget }),
        };

  /** Spread on the chart: focusable, the arrows move the cursor, leaving clears it. */
  const surface = {
    tabIndex: count > 0 ? 0 : undefined,
    onPointerLeave: clear,
    onBlur: (event: FocusEvent<Element>) => {
      if (!event.currentTarget.contains(event.relatedTarget)) clear();
    },
    onKeyDown: (event: KeyboardEvent<Element>) => {
      if (count === 0) return;
      const current = active?.index;

      const next =
        event.key === "ArrowRight" || event.key === "ArrowDown"
          ? current === undefined
            ? 0
            : Math.min(count - 1, current + 1)
          : event.key === "ArrowLeft" || event.key === "ArrowUp"
            ? current === undefined
              ? count - 1
              : Math.max(0, current - 1)
            : event.key === "Home"
              ? 0
              : event.key === "End"
                ? count - 1
                : null;

      if (event.key === "Escape" && active) {
        event.preventDefault();
        event.stopPropagation();
        clear();

        return;
      }

      if (next === null) return;
      event.preventDefault();
      select(next);
    },
  };

  return { index: active?.index ?? null, anchor: active?.anchor ?? null, mark, surface, clear };
}

/**
 * The card beside the mark under the cursor. It never takes focus and lets the
 * pointer through, so moving across the chart is not interrupted by it.
 */
export function ChartCard({
  anchor,
  children,
  side = "top",
  className,
}: {
  anchor: Element | null;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}) {
  return (
    <PopoverPrimitive.Root open={anchor !== null && children !== null}>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          anchor={anchor}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className="pointer-events-none isolate layer-popover"
        >
          <PopoverPrimitive.Popup
            initialFocus={false}
            finalFocus={false}
            data-slot="chart-card"
            className={cn(
              "chart-card pointer-events-none flex max-w-64 min-w-0 flex-col gap-1 rounded-lg bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden",
              className,
            )}
          >
            {children}
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** The card's heading: what the mark is (a day, an hour, a run). */
export function ChartCardTitle({ children }: { children: ReactNode }) {
  return <p className="font-medium text-foreground">{children}</p>;
}

/** One labelled value in the card, with the swatch of its series. */
export function ChartCardRow({
  swatch,
  label,
  value,
  muted = false,
}: {
  swatch?: ReactNode;
  label: ReactNode;
  value?: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className={cn("flex min-w-0 items-center gap-2", muted && "text-muted-foreground")}>
      {swatch}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {value !== undefined && <span className="shrink-0 tabular-nums">{value}</span>}
    </div>
  );
}
